import React, { useState } from "react";
import {
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components";
import { FormInput, FormTextarea, FormSelect } from "@/components";
import { toast } from "react-hot-toast";
import type {
  ApiEndpoint,
  EndpointParameter,
  EndpointResponse,
  HttpMethod,
  HttpStatusCode,
  FieldType,
} from "@/features/Projects/models/project.types";
import { EndpointEditorProps } from "../schemaPhase.types";
import {
  HTTP_METHOD_OPTIONS,
  HTTP_STATUS_OPTIONS,
  PARAMETER_TYPE_OPTIONS,
  FIELD_TYPE_OPTIONS,
} from "../schemaPhase.definitions";

export function EndpointEditor({ endpoint, onSave, onCancel }: EndpointEditorProps) {
  const [editedEndpoint, setEditedEndpoint] = useState<ApiEndpoint>(
    endpoint || {
      id: `endpoint-${Date.now()}`,
      path: "",
      method: "GET",
      summary: "",
      description: "",
      parameters: [],
      responses: [
        {
          statusCode: 200,
          description: "Sucesso",
          example: {},
        },
      ],
      tags: [],
      requiresAuth: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  );

  const [showDeleteParamConfirm, setShowDeleteParamConfirm] = useState<
    string | null
  >(null);
  const [showDeleteResponseConfirm, setShowDeleteResponseConfirm] = useState<
    number | null
  >(null);

  const handleEndpointChange = (field: keyof ApiEndpoint, value: any) => {
    setEditedEndpoint((prev) => ({ ...prev, [field]: value }));
  };

  const handleParameterChange = (
    paramIndex: number,
    field: keyof EndpointParameter,
    value: any
  ) => {
    const updatedParams = [...editedEndpoint.parameters];
    updatedParams[paramIndex] = {
      ...updatedParams[paramIndex],
      [field]: value,
    };
    setEditedEndpoint((prev) => ({ ...prev, parameters: updatedParams }));
  };

  const handleResponseChange = (
    responseIndex: number,
    field: keyof EndpointResponse,
    value: any
  ) => {
    const updatedResponses = [...editedEndpoint.responses];
    updatedResponses[responseIndex] = {
      ...updatedResponses[responseIndex],
      [field]: value,
    };
    setEditedEndpoint((prev) => ({ ...prev, responses: updatedResponses }));
  };

  const addParameter = () => {
    const newParam: EndpointParameter = {
      name: "",
      type: "query",
      dataType: "string",
      required: false,
      description: "",
      example: "",
    };
    setEditedEndpoint((prev) => ({
      ...prev,
      parameters: [...prev.parameters, newParam],
    }));
  };

  const removeParameter = (paramIndex: number) => {
    setEditedEndpoint((prev) => ({
      ...prev,
      parameters: prev.parameters.filter((_, index) => index !== paramIndex),
    }));
    setShowDeleteParamConfirm(null);
  };

  const addResponse = () => {
    const newResponse: EndpointResponse = {
      statusCode: 201,
      description: "",
      example: {},
    };
    setEditedEndpoint((prev) => ({
      ...prev,
      responses: [...prev.responses, newResponse],
    }));
  };

  const removeResponse = (responseIndex: number) => {
    if (editedEndpoint.responses.length <= 1) {
      toast.error("Um endpoint deve ter pelo menos uma resposta");
      return;
    }

    setEditedEndpoint((prev) => ({
      ...prev,
      responses: prev.responses.filter((_, index) => index !== responseIndex),
    }));
    setShowDeleteResponseConfirm(null);
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tags = e.target.value
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag);
    setEditedEndpoint((prev) => ({ ...prev, tags }));
  };

  const handleSave = () => {
    // Validações básicas
    if (!editedEndpoint.path.trim()) {
      toast.error("Caminho do endpoint é obrigatório");
      return;
    }
    if (!editedEndpoint.summary.trim()) {
      toast.error("Resumo do endpoint é obrigatório");
      return;
    }

    // Validar parâmetros
    for (let i = 0; i < editedEndpoint.parameters.length; i++) {
      const param = editedEndpoint.parameters[i];
      if (!param.name.trim()) {
        toast.error(`Nome do parâmetro na posição ${i + 1} é obrigatório`);
        return;
      }
    }

    // Validar respostas
    for (let i = 0; i < editedEndpoint.responses.length; i++) {
      const response = editedEndpoint.responses[i];
      if (!response.description.trim()) {
        toast.error(
          `Descrição da resposta ${response.statusCode} é obrigatória`
        );
        return;
      }
    }

    const finalEndpoint = {
      ...editedEndpoint,
      updatedAt: new Date().toISOString(),
    };

    onSave(finalEndpoint);
  };

  return (
    <div className="bg-white border-2 border-green-200 rounded-lg p-6 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-gray-900">
          {endpoint ? "Editar Endpoint" : "Novo Endpoint"}
        </h4>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            leftIcon={<XMarkIcon className="h-4 w-4" />}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            leftIcon={<CheckIcon className="h-4 w-4" />}
          >
            Salvar
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Informações básicas do endpoint */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormSelect
            label="Método HTTP"
            value={editedEndpoint.method}
            onChange={(e) =>
              handleEndpointChange("method", e.target.value as HttpMethod)
            }
            options={HTTP_METHOD_OPTIONS}
            required
          />
          <FormInput
            label="Caminho"
            placeholder="Ex: /api/users/{id}"
            value={editedEndpoint.path}
            onChange={(e) => handleEndpointChange("path", e.target.value)}
            required
          />
          <FormInput
            label="Resumo"
            placeholder="Ex: Listar usuários"
            value={editedEndpoint.summary}
            onChange={(e) => handleEndpointChange("summary", e.target.value)}
            required
          />
        </div>

        <FormTextarea
          label="Descrição"
          placeholder="Descreva o que este endpoint faz..."
          value={editedEndpoint.description}
          onChange={(e) => handleEndpointChange("description", e.target.value)}
          rows={2}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Tags (separadas por vírgula)"
            placeholder="Ex: Users, Authentication"
            value={editedEndpoint.tags.join(", ")}
            onChange={handleTagsChange}
          />
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={editedEndpoint.requiresAuth}
              onChange={(e) =>
                handleEndpointChange("requiresAuth", e.target.checked)
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Requer Autenticação</span>
          </label>
        </div>

        {/* Parâmetros */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h5 className="font-medium text-gray-900">Parâmetros</h5>
            <Button
              variant="outline"
              size="sm"
              onClick={addParameter}
              leftIcon={<PlusIcon className="h-4 w-4" />}
            >
              Adicionar Parâmetro
            </Button>
          </div>

          <div className="space-y-3">
            {editedEndpoint.parameters.map((param, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h6 className="font-medium text-gray-800">
                    Parâmetro #{index + 1}
                  </h6>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteParamConfirm(index.toString())}
                    leftIcon={<TrashIcon className="h-4 w-4" />}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remover
                  </Button>
                </div>

                {/* Confirmação de remoção de parâmetro */}
                {showDeleteParamConfirm === index.toString() && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                    <p className="text-sm text-red-800 mb-2">
                      Tem certeza que deseja remover o parâmetro "
                      {param.name || "Sem nome"}"?
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeParameter(index)}
                      >
                        Sim, remover
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteParamConfirm(null)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <FormInput
                    label="Nome"
                    placeholder="Ex: id, email"
                    value={param.name}
                    onChange={(e) =>
                      handleParameterChange(index, "name", e.target.value)
                    }
                    required
                  />
                  <FormSelect
                    label="Local"
                    value={param.type}
                    onChange={(e) =>
                      handleParameterChange(index, "type", e.target.value)
                    }
                    options={PARAMETER_TYPE_OPTIONS}
                    required
                  />
                  <FormSelect
                    label="Tipo de Dados"
                    value={param.dataType}
                    onChange={(e) =>
                      handleParameterChange(
                        index,
                        "dataType",
                        e.target.value as FieldType
                      )
                    }
                    options={FIELD_TYPE_OPTIONS}
                    required
                  />
                  <FormInput
                    label="Exemplo"
                    placeholder="Valor exemplo"
                    value={param.example || ""}
                    onChange={(e) =>
                      handleParameterChange(index, "example", e.target.value)
                    }
                  />
                </div>

                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormTextarea
                    label="Descrição"
                    placeholder="Descreva este parâmetro..."
                    value={param.description}
                    onChange={(e) =>
                      handleParameterChange(
                        index,
                        "description",
                        e.target.value
                      )
                    }
                    rows={2}
                  />
                  <label className="flex items-center space-x-2 mt-6">
                    <input
                      type="checkbox"
                      checked={param.required}
                      onChange={(e) =>
                        handleParameterChange(
                          index,
                          "required",
                          e.target.checked
                        )
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Obrigatório</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Respostas */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h5 className="font-medium text-gray-900">Respostas</h5>
            <Button
              variant="outline"
              size="sm"
              onClick={addResponse}
              leftIcon={<PlusIcon className="h-4 w-4" />}
            >
              Adicionar Resposta
            </Button>
          </div>

          <div className="space-y-3">
            {editedEndpoint.responses.map((response, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h6 className="font-medium text-gray-800">
                    Resposta #{index + 1} - {response.statusCode}
                  </h6>
                  {editedEndpoint.responses.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDeleteResponseConfirm(index)}
                      leftIcon={<TrashIcon className="h-4 w-4" />}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remover
                    </Button>
                  )}
                </div>

                {/* Confirmação de remoção de resposta */}
                {showDeleteResponseConfirm === index && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                    <p className="text-sm text-red-800 mb-2">
                      Tem certeza que deseja remover a resposta{" "}
                      {response.statusCode}?
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeResponse(index)}
                      >
                        Sim, remover
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteResponseConfirm(null)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormSelect
                    label="Status Code"
                    value={response.statusCode.toString()}
                    onChange={(e) =>
                      handleResponseChange(
                        index,
                        "statusCode",
                        parseInt(e.target.value) as HttpStatusCode
                      )
                    }
                    options={HTTP_STATUS_OPTIONS}
                    required
                  />
                  <FormInput
                    label="Schema/Modelo"
                    placeholder="Ex: UserResponse, ErrorResponse"
                    value={response.schema || ""}
                    onChange={(e) =>
                      handleResponseChange(index, "schema", e.target.value)
                    }
                  />
                </div>

                <div className="mt-3">
                  <FormTextarea
                    label="Descrição"
                    placeholder="Descreva esta resposta..."
                    value={response.description}
                    onChange={(e) =>
                      handleResponseChange(index, "description", e.target.value)
                    }
                    rows={2}
                    required
                  />
                </div>

                <div className="mt-3">
                  <FormTextarea
                    label="Exemplo (JSON)"
                    placeholder='{"message": "Success", "data": {...}}'
                    value={
                      typeof response.example === "string"
                        ? response.example
                        : JSON.stringify(response.example, null, 2)
                    }
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        handleResponseChange(index, "example", parsed);
                      } catch {
                        handleResponseChange(index, "example", e.target.value);
                      }
                    }}
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}